"use client";

import { useRef, useState, useEffect, useMemo } from "react";
import Link from "next/link";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

// Floating geometry for services page - different arrangement
function FloatingGeometry() {
  const groupRef = useRef<THREE.Group>(null);
  const mesh1Ref = useRef<THREE.Mesh>(null);
  const mesh2Ref = useRef<THREE.Mesh>(null);
  const mesh3Ref = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (groupRef.current) {
      groupRef.current.rotation.y = t * 0.015;
    }
    if (mesh1Ref.current) {
      mesh1Ref.current.rotation.x = t * 0.08;
      mesh1Ref.current.rotation.z = t * 0.04;
      mesh1Ref.current.position.y = Math.sin(t * 0.25) * 0.4;
    }
    if (mesh2Ref.current) {
      mesh2Ref.current.rotation.y = t * 0.06;
      mesh2Ref.current.position.y = Math.sin(t * 0.3 + 1) * 0.3;
    }
    if (mesh3Ref.current) {
      mesh3Ref.current.rotation.x = t * 0.05;
      mesh3Ref.current.rotation.y = t * 0.03;
      mesh3Ref.current.position.y = Math.sin(t * 0.28 + 2) * 0.35;
    }
  });

  return (
    <group ref={groupRef}>
      {/* Main icosahedron - positioned left */}
      <mesh ref={mesh1Ref} position={[-2.5, 0.5, 0]}>
        <icosahedronGeometry args={[2, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.35} transparent />
      </mesh>

      {/* Secondary octahedron - positioned right */}
      <mesh ref={mesh2Ref} position={[2.5, -0.5, -1]}>
        <octahedronGeometry args={[1.8, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.25} transparent />
      </mesh>

      {/* Tertiary tetrahedron - center bottom */}
      <mesh ref={mesh3Ref} position={[0, -2, -1.5]}>
        <tetrahedronGeometry args={[1.4, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.2} transparent />
      </mesh>
    </group>
  );
}

// Dot grid
function DotGrid() {
  const pointsRef = useRef<THREE.Points>(null);

  const positions = useMemo(() => {
    const pos = [];
    const gridSize = 20;
    const spacing = 0.8;
    for (let x = -gridSize / 2; x < gridSize / 2; x++) {
      for (let y = -gridSize / 2; y < gridSize / 2; y++) {
        pos.push(x * spacing, y * spacing, -5);
      }
    }
    return new Float32Array(pos);
  }, []);

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.z = state.clock.elapsedTime * 0.008;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
        />
      </bufferGeometry>
      <pointsMaterial color="#1a1a1a" size={0.015} opacity={0.08} transparent />
    </points>
  );
}

function Scene() {
  return (
    <>
      <FloatingGeometry />
      <DotGrid />
    </>
  );
}

// Typewriter hook
function useTypewriter(text: string, speed: number = 50, delay: number = 0) {
  const [displayedText, setDisplayedText] = useState("");
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayedText("");
    setIsComplete(false);

    const startTimeout = setTimeout(() => {
      let index = 0;
      const interval = setInterval(() => {
        if (index < text.length) {
          setDisplayedText(text.slice(0, index + 1));
          index++;
        } else {
          setIsComplete(true);
          clearInterval(interval);
        }
      }, speed);

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(startTimeout);
  }, [text, speed, delay]);

  return { displayedText, isComplete };
}

// Animated wireframe shape for featured card
function AnimatedWireframe() {
  return (
    <div className="relative w-48 h-48 md:w-56 md:h-56">
      {/* Outer rotating icosahedron-like shape */}
      <div
        className="absolute inset-0"
        style={{
          animation: 'wireframe-spin 20s linear infinite',
        }}
      >
        <svg viewBox="0 0 100 100" className="w-full h-full">
          {/* Main hexagon */}
          <polygon
            points="50,5 90,27.5 90,72.5 50,95 10,72.5 10,27.5"
            fill="none"
            stroke="rgba(255,255,255,0.4)"
            strokeWidth="0.5"
          />
          {/* Inner connections */}
          <line x1="50" y1="5" x2="50" y2="95" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />
          <line x1="10" y1="27.5" x2="90" y2="72.5" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />
          <line x1="90" y1="27.5" x2="10" y2="72.5" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />
          {/* Center hexagon */}
          <polygon
            points="50,25 70,37.5 70,62.5 50,75 30,62.5 30,37.5"
            fill="none"
            stroke="rgba(255,255,255,0.5)"
            strokeWidth="0.5"
          />
        </svg>
      </div>

      {/* Middle rotating shape - opposite direction */}
      <div
        className="absolute inset-4"
        style={{
          animation: 'wireframe-spin-reverse 15s linear infinite',
        }}
      >
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <polygon
            points="50,10 85,30 85,70 50,90 15,70 15,30"
            fill="none"
            stroke="rgba(255,255,255,0.35)"
            strokeWidth="0.5"
          />
          <line x1="50" y1="10" x2="50" y2="90" stroke="rgba(255,255,255,0.25)" strokeWidth="0.5" />
          <line x1="15" y1="30" x2="85" y2="70" stroke="rgba(255,255,255,0.25)" strokeWidth="0.5" />
          <line x1="85" y1="30" x2="15" y2="70" stroke="rgba(255,255,255,0.25)" strokeWidth="0.5" />
        </svg>
      </div>

      {/* Inner pulsing shape */}
      <div
        className="absolute inset-12"
        style={{
          animation: 'wireframe-spin 12s linear infinite, wireframe-pulse 4s ease-in-out infinite',
        }}
      >
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <polygon
            points="50,15 80,35 80,65 50,85 20,65 20,35"
            fill="none"
            stroke="rgba(255,255,255,0.6)"
            strokeWidth="0.8"
          />
          {/* Diamond in center */}
          <polygon
            points="50,30 65,50 50,70 35,50"
            fill="none"
            stroke="rgba(255,255,255,0.7)"
            strokeWidth="0.5"
          />
        </svg>
      </div>
    </div>
  );
}

// Scroll-animated service card
function ServiceCard({ service, index, featured = false }: { service: typeof services[0]; index: number; featured?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Stagger the animation based on index
          setTimeout(() => setIsVisible(true), index * 100);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [index]);

  if (featured) {
    return (
      <Link href={service.href} className="block">
        <div
          ref={ref}
          className="group bg-gradient-to-br from-[#ff6900] to-[#c2410c] rounded-2xl p-8 md:p-12 text-white transition-all duration-700"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
          }}
        >
          <div className="grid md:grid-cols-[3fr_2fr] gap-8 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">Featured</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold mb-4">
                {service.title}
              </h2>
              <p className="text-white/80 text-lg mb-6 leading-relaxed">
                {service.description}
              </p>
              <ul className="space-y-3 mb-6">
                {service.details.map((detail, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/90">
                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {detail}
                  </li>
                ))}
              </ul>
              <span className="inline-flex items-center gap-2 text-sm font-medium text-white opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                Learn More
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </div>
            <div className="flex items-center justify-center">
              <AnimatedWireframe />
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link href={service.href} className="block">
      <div
        ref={ref}
        className="group bg-white rounded-2xl p-8 md:p-10 border border-[#1a1a1a]/5 hover:border-[#ff6900]/20 hover:shadow-lg hover:shadow-[#ff6900]/5 transition-all duration-500 h-full"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        }}
      >
        <div className="flex items-start justify-between mb-6">
          <div className="w-10 h-10 text-[#ff6900] group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
            {service.icon}
          </div>
          <span className="text-sm text-[#1a1a1a]/20 font-mono">
            0{index + 1}
          </span>
        </div>
        <h2 className="text-2xl font-semibold mb-3 group-hover:text-[#ff6900] transition-colors">
          {service.title}
        </h2>
        <p className="text-[#1a1a1a]/70 mb-6 leading-relaxed">
          {service.description}
        </p>
        <ul className="space-y-2 mb-6">
          {service.details.map((detail, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-[#1a1a1a]/70">
              <svg className="w-4 h-4 text-[#ff6900] mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {detail}
            </li>
          ))}
        </ul>
        <span className="inline-flex items-center gap-2 text-sm font-medium text-[#ff6900] opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          Learn More
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </span>
      </div>
    </Link>
  );
}

const services = [
  {
    id: "ai-native",
    href: "/services/full-stack-web-development",
    title: "AI-Native Software Delivery",
    description: "We deliver enterprise software with AI embedded throughout the delivery process, enabling faster iteration without sacrificing quality, security, or long-term maintainability.",
    details: [
      "Full-stack development with AI integrated from day one",
      "Faster iteration cycles through AI-assisted development and review",
      "Reduced time-to-market without compromising quality or security",
      "Seamless integration with existing enterprise systems and data platforms",
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
      </svg>
    ),
  },
  {
    id: "data",
    href: "/services/data-services",
    title: "Data Services",
    description: "We design and modernize data platforms teams can trust, scale, and extend over time, supporting analytics, operational reporting, and AI-driven use cases without repeated rework.",
    details: [
      "Enterprise data architecture and pipeline design with clear ownership and governance",
      "Reliable ETL and ELT pipelines designed for scale, monitoring, and change over time",
      "Data warehouse and lake implementations aligned to analytics, reporting, and downstream applications",
      "Real-time and near-real-time analytics infrastructure where latency and accuracy matter",
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
      </svg>
    ),
  },
  {
    id: "design",
    href: "/services/ux-ui-design",
    title: "UX/UI Design",
    description: "AI-assisted design that reduces handoffs, accelerates development, and helps teams validate decisions earlier in the delivery process.",
    details: [
      "User research and journey mapping to align teams around real user needs",
      "Design systems and component libraries that scale across products",
      "Interactive prototyping to validate decisions early and reduce rework",
      "Accessibility-first design practices aligned with enterprise standards",
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
      </svg>
    ),
  },
  {
    id: "cms",
    href: "/services/content-management",
    title: "Content Management",
    description: "Modern CMS and headless solutions designed to scale content operations, reduce implementation friction, and support evolving digital experiences.",
    details: [
      "Headless CMS architecture",
      "Sitecore, Contentstack, Contentful, and Sanity implementations",
      "Content modeling and structured migration",
      "AI-powered content workflows and automation",
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
  },
  {
    id: "mobile",
    href: "/services/mobile-app-development",
    title: "Mobile App Development",
    description: "iOS and Android applications built with AI-assisted workflows that reduce handoffs, accelerate delivery, and support long-term maintainability.",
    details: [
      "Native iOS and Android development",
      "Cross-platform solutions with React Native",
      "App Store optimization and deployment",
      "Ongoing maintenance and iteration",
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
      </svg>
    ),
  },
  {
    id: "ai-ml",
    href: "/services/ai-ml",
    title: "AI/ML",
    description: "Applied machine learning capabilities focused on production-ready systems, including RAG architectures, predictive analytics, and domain-specific AI solutions.",
    details: [
      "Custom ML model development for real-world use cases",
      "RAG and LLM integration with existing data platforms",
      "Predictive analytics and forecasting solutions",
      "Applied AI use cases tailored to domain-specific data",
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
  },
];

export default function ServicesPage() {
  const [glowPos, setGlowPos] = useState({ x: 0, y: 0 });
  const targetPos = useRef({ x: 0, y: 0 });

  // Typewriter effect for hero headline
  const { displayedText: heroText, isComplete: heroComplete } = useTypewriter(
    "AI-native delivery for enterprise teams",
    45,
    300
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      targetPos.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      targetPos.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    let animationId: number;
    const currentPos = { x: 0, y: 0 };

    const animate = () => {
      currentPos.x += (targetPos.current.x - currentPos.x) * 0.03;
      currentPos.y += (targetPos.current.y - currentPos.y) * 0.03;
      setGlowPos({ x: currentPos.x, y: currentPos.y });
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#1a1a1a] relative overflow-hidden">
      {/* Three.js Canvas Background */}
      <div className="fixed inset-0 z-0" aria-hidden="true">
        <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
          <Scene />
        </Canvas>
      </div>

      <div className="relative z-10">
        <Navigation />

        <main id="main-content">

        {/* Hero */}
        <section className="pt-32 pb-16 md:pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl">
            {/* Reserve space for full 2-line title to prevent layout shift */}
            <div className="relative min-h-[2.4em] md:min-h-[2.4em] lg:min-h-[2.4em]">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.1] tracking-tight mb-6 lg:whitespace-nowrap">
                {heroText.includes("modern teams") ? (
                  <>
                    {heroText.slice(0, heroText.indexOf("modern teams"))}
                    <span className="text-[#ff6900]">{heroText.slice(heroText.indexOf("modern teams"))}</span>
                  </>
                ) : (
                  heroText
                )}
                <span
                  className={`inline-block w-[3px] h-[0.9em] bg-[#ff6900] ml-1 align-middle ${heroComplete ? 'opacity-0' : 'animate-pulse'}`}
                />
              </h1>
            </div>
            <p
              className="text-xl text-[#1a1a1a]/70 leading-relaxed transition-all duration-500"
              style={{
                opacity: heroComplete ? 1 : 0,
                transform: heroComplete ? 'translateY(0)' : 'translateY(10px)',
              }}
            >
              We help teams adopt modern AI-driven development practices without
              sacrificing project timelines. From strategy and design through build and launch.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section
        className="pb-24 px-6 transition-all duration-700"
        style={{
          opacity: heroComplete ? 1 : 0,
          transform: heroComplete ? 'translateY(0)' : 'translateY(20px)',
        }}
      >
        <div className="max-w-6xl mx-auto">
          {/* Featured service spotlight */}
          <div className="mb-8">
            <ServiceCard service={services[0]} index={0} featured />
          </div>

          {/* Other services grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.slice(1).map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index + 1} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-24 px-6 bg-gradient-to-r from-[#ff6900] to-[#c2410c] text-white transition-opacity duration-700"
        style={{ opacity: heroComplete ? 1 : 0 }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
            Ready to modernize your delivery?
          </h2>
          <p className="text-xl text-white/70 mb-10">
            Let&apos;s discuss how our AI-native approach can accelerate your next project.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white text-[#1a1a1a] font-medium hover:bg-white/90 transition-colors"
          >
            Get in touch
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </section>
        </main>

        <div
          className="transition-opacity duration-700"
          style={{ opacity: heroComplete ? 1 : 0 }}
        >
          <Footer />
        </div>
      </div>
    </div>
  );
}
